import { useFormik } from "formik";
import { LngLat } from "mapbox-gl";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useMapMarkerStore, usePipedriveStore, useSaleCompStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useNavigateToSaleComp from "../../hooks/useNavigateToSaleComp";
import useProperty from "../../hooks/useProperty";
import useSaleCompCreate from "../../hooks/useSaleCompCreate";
import useSaleCompValidationSchema from "../../hooks/useSaleCompValidationSchema";
import DetailsType from "../../interfaces/DetailsType";
import { GeoCoordinate } from "../../interfaces/GeoCoordinate";
import { ISaleCompState, SaleCompState } from "../../interfaces/ISaleComp";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import { CreateControls } from "../EditControls";
import SaleCompIcon from "../icons/SaleCompIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";
import SaleCompFields from "./SaleCompFields";

function SaleCompCreate() {
    const { longitude, latitude } = useParams();
    const navigate = useNavigate();
    const navigateToSaleComp = useNavigateToSaleComp();
    const saleCompStore = useSaleCompStore();
    const pipedriveStore = usePipedriveStore();
    const mapMarkerStore = useMapMarkerStore();
    const coordinates = useMemo(() => new LngLat(Number(longitude), Number(latitude)), [longitude, latitude]);
    const validationSchema = useSaleCompValidationSchema();
    const { property, isLoading, fetchProperty } = useProperty(coordinates);
    const createSaleComp = useSaleCompCreate();
    const initialState: ISaleCompState | null = useMemo(() => computed(() => property ? {
        ...SaleCompState.empty(),
        address: property.address,
        regridId: property.regridId,
        buildingSize: property.buildingSize,
        parcelSizeAcres: property.landSize,
    } : null), [property]).get();

    const formik = useFormik<ISaleCompState>({
        initialValues: SaleCompState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            const createdLeaseCompId = await createSaleComp(values, property!.regridId);
            navigateToSaleComp(createdLeaseCompId, true);
        },
    });

    useEffect(() => {
        if (initialState)
            formik.resetForm({ values: initialState });
    }, [initialState]);

    useEffect(() => {
        fetchProperty();
        mapMarkerStore.draw(new GeoCoordinate(coordinates.lat, coordinates.lng));
        return () => mapMarkerStore.clear();
    }, [coordinates]);

    const close = () => navigate("/");
    const cancel = () => navigate(-1);
    useClosable(ClosableOrder.Medium, cancel);

    if (isLoading)
        return <OnMapSpinner />;

    if (!property)
        return null;

    return (
        <DetailsPanel type={DetailsType.SaleCompCreate} onClose={close}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <SaleCompIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.primary}>Add New Sale Comp</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <SaleCompFields formik={formik} />
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <CreateControls
                        isStateChanged={formik.dirty || !mapMarkerStore.isMatch(new GeoCoordinate(coordinates.lat, coordinates.lng))}
                        isCreateLoading={saleCompStore.isSaleCompsLoading.creating! || pipedriveStore.isOrganizationsLoading.creating!}
                        onCreate={formik.handleSubmit}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}></div>
            </div>
        </DetailsPanel>
    );
}

export default observer(SaleCompCreate);